import {
	AlertTriangle,
	ChevronDown,
	ChevronRight,
	Download,
	FileSpreadsheet,
	XCircle,
} from "lucide-react";
import React, { useState } from "react";
import { chatApi } from "../services/chatApi";
import CitedExcelCell, {
	displayValueForCitedCell,
	isCitedCellValue,
} from "./CitedExcelCell";
import type { ModifiedWorkbookBlock } from "./Chat/Chat.types";

interface ModifiedWorkbookBlockViewProps {
	block: ModifiedWorkbookBlock;
	chatId: string | null;
}

const formatDuration = (ms: number): string => {
	if (!ms) return "";
	if (ms < 1000) return `${ms}ms`;
	const seconds = ms / 1000;
	if (seconds < 60) return `${seconds.toFixed(1)}s`;
	const mins = Math.floor(seconds / 60);
	const remSec = Math.round(seconds - mins * 60);
	return `${mins}m ${remSec}s`;
};

const summaryBullets = (
	manifest: Record<string, unknown>,
	recalcStatus: string,
): string[] => {
	const out: string[] = [];
	const sheetsAdded = manifest.sheets_added;
	if (Array.isArray(sheetsAdded) && sheetsAdded.length > 0) {
		out.push(`Added sheets: ${sheetsAdded.join(", ")}`);
	}
	const sheetsRemoved = manifest.sheets_removed;
	if (Array.isArray(sheetsRemoved) && sheetsRemoved.length > 0) {
		out.push(`Removed sheets: ${sheetsRemoved.join(", ")}`);
	}
	const sheetsRenamed = manifest.sheets_renamed;
	if (Array.isArray(sheetsRenamed) && sheetsRenamed.length > 0) {
		const renames = sheetsRenamed
			.map((r) => {
				if (r && typeof r === "object" && "from" in r && "to" in r) {
					const rec = r as { from: unknown; to: unknown };
					return `${String(rec.from)} → ${String(rec.to)}`;
				}
				return null;
			})
			.filter(Boolean) as string[];
		if (renames.length > 0) out.push(`Renamed sheets: ${renames.join(", ")}`);
	}
	if (typeof manifest.cells_changed_count === "number") {
		out.push(`${manifest.cells_changed_count} cells changed`);
	}
	if (typeof manifest.formula_cells_count === "number" && manifest.formula_cells_count > 0) {
		out.push(`${manifest.formula_cells_count} formulas written`);
	}
	const ranges = manifest.ranges_changed;
	if (Array.isArray(ranges) && ranges.length > 0) {
		const shown = ranges.slice(0, 3).map(String).join(", ");
		const suffix = ranges.length > 3 ? `, +${ranges.length - 3} more` : "";
		out.push(`Ranges: ${shown}${suffix}`);
	}
	// "not_attempted" is the normal state for a from-scratch build (Excel
	// evaluates on open) — telemetry, not information. Only surface recalc
	// when it actually ran or actually failed.
	if (recalcStatus && recalcStatus !== "not_attempted") {
		out.push(`Recalc: ${recalcStatus}`);
	}
	return out;
};

const colLabel = (idx: number): string => {
	let n = idx;
	let s = "";
	while (n >= 0) {
		s = String.fromCharCode(65 + (n % 26)) + s;
		n = Math.floor(n / 26) - 1;
	}
	return s;
};

const cellDisplay = (v: unknown): string => {
	if (isCitedCellValue(v)) return displayValueForCitedCell(v);
	if (v === null || v === undefined) return "";
	if (typeof v === "number") return String(v);
	if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
	return String(v);
};

const ModifiedWorkbookBlockView: React.FC<ModifiedWorkbookBlockViewProps> = ({
	block,
	chatId,
}) => {
	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadError, setDownloadError] = useState<string | null>(null);
	// Collapsed by default, for every workbook card. The grid almost always
	// restates the cited markdown table the answer already renders below it,
	// so showing both by default is duplication the reader has to skim past.
	// The card is the artifact and the download; the grid is a spot check, and
	// it is one click away. No per-origin logic — a rule that fires sometimes
	// is a rule the reader cannot learn.
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	const handleDownload = async () => {
		setDownloadError(null);
		setIsDownloading(true);
		try {
			if (!chatId) throw new Error("Chat id missing");
			// Fetch the bytes through the same-origin proxy route rather than
			// navigating to the GCS signed URL. Browsers ignore the anchor's
			// ``download`` attribute for cross-origin URLs and fall back to the
			// URL's last path segment — the stored object name
			// (``{chat_id}_{message_id}.xlsx``) — so the user got ``319_969.xlsx``
			// instead of the name on the card. A blob URL is same-origin, so the
			// attribute is honoured and the card's filename is what gets saved.
			const blob = await chatApi.downloadWorkbookArtifact(
				chatId,
				block.block_id,
			);
			const objectUrl = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = objectUrl;
			link.download = block.filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(objectUrl);
		} catch (err) {
			console.error("Modified workbook download failed:", err);
			setDownloadError("Download failed. Please try again.");
		} finally {
			setIsDownloading(false);
		}
	};

	const bullets = summaryBullets(block.manifest_summary, block.recalc_status);
	const duration = formatDuration(block.duration_ms);
	const preview = block.preview;
	const hasPreview = !!preview && preview.rows.length > 0;
	const previewId = `workbook-preview-${block.block_id}`;

	return (
		<div
			data-export-exclude
			className="rounded-lg border border-gray-200 bg-white p-4 my-3 flex flex-col gap-3"
		>
			<div className="flex items-center justify-between gap-4">
				<div className="flex items-center gap-3 min-w-0">
					<FileSpreadsheet className="w-5 h-5 text-teal-600 shrink-0" />
					<div className="min-w-0">
						<div className="text-sm font-medium text-gray-900 truncate">
							{block.filename}
						</div>
						<div className="text-xs text-gray-500">
							Excel workbook{duration ? ` • ${duration}` : ""}
						</div>
					</div>
				</div>
				<button
					onClick={handleDownload}
					disabled={isDownloading || !chatId}
					className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 shrink-0"
				>
					<Download className="w-4 h-4" />
					{isDownloading ? "Opening..." : "Download"}
				</button>
			</div>

			{bullets.length > 0 && (
				<ul className="text-sm text-gray-700 list-disc pl-5 space-y-0.5">
					{bullets.map((b, i) => (
						<li key={i}>{b}</li>
					))}
				</ul>
			)}

			{block.warnings.length > 0 && (
				<div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
					<AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
					<div className="flex flex-col gap-1">
						{block.warnings.map((w, i) => (
							<span key={i}>{w}</span>
						))}
					</div>
				</div>
			)}

			{block.formula_error_summary.length > 0 && (
				<div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex gap-2">
					<XCircle className="w-4 h-4 shrink-0 mt-0.5" />
					<div className="flex flex-col gap-1">
						<span className="font-medium">Formula errors:</span>
						{block.formula_error_summary.slice(0, 8).map((fe, i) => (
							<span key={i} className="font-mono text-xs">
								{String(fe.cell ?? "?")}: {String(fe.error ?? "?")}
							</span>
						))}
						{block.formula_error_summary.length > 8 && (
							<span className="text-xs">
								+{block.formula_error_summary.length - 8} more
							</span>
						)}
					</div>
				</div>
			)}

			{hasPreview && (
				<button
					type="button"
					onClick={() => setIsPreviewOpen((open) => !open)}
					aria-expanded={isPreviewOpen}
					aria-controls={previewId}
					className="inline-flex items-center gap-1 self-start text-xs text-gray-500 hover:text-gray-700"
				>
					{isPreviewOpen ? (
						<ChevronDown className="w-3.5 h-3.5" />
					) : (
						<ChevronRight className="w-3.5 h-3.5" />
					)}
					{isPreviewOpen ? "Hide preview" : "Show preview"}
				</button>
			)}

			{hasPreview && isPreviewOpen && preview ? (
				<div
					id={previewId}
					className="overflow-auto max-h-[360px] border border-gray-200 rounded-md"
				>
					<table className="text-xs border-collapse w-full">
						<thead className="sticky top-0 bg-gray-50">
							<tr>
								<th className="border border-gray-200 px-2 py-1 text-left text-gray-500 font-normal w-10" />
								{Array.from(
									{ length: preview.rows[0]?.length ?? 0 },
									(_, i) => (
										<th
											key={i}
											className="border border-gray-200 px-2 py-1 text-left text-gray-500 font-normal"
										>
											{colLabel(i)}
										</th>
									),
								)}
							</tr>
						</thead>
						<tbody>
							{preview.rows.map((row, rIdx) => (
								<tr key={rIdx}>
									<td className="border border-gray-200 px-2 py-1 text-gray-400 bg-gray-50 text-right">
										{rIdx + 1}
									</td>
									{row.map((cell, cIdx) => (
										<td
											key={cIdx}
											className="border border-gray-200 px-2 py-1 text-gray-800 whitespace-nowrap"
										>
											{isCitedCellValue(cell) ? (
												<CitedExcelCell cell={cell} chatId={chatId} />
											) : (
												cellDisplay(cell)
											)}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
					<div className="px-2 py-1 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
						Sheet: {preview.sheet} • {preview.row_count} rows ×{" "}
						{preview.col_count} cols
						{preview.truncated_cols ? " (columns truncated)" : ""}
					</div>
				</div>
			) : null}

			{downloadError && (
				<div className="text-xs text-red-600">{downloadError}</div>
			)}
		</div>
	);
};

export default ModifiedWorkbookBlockView;
